import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  viewMode: 'calendar',
  viewingRecordId: null,
  message: { text: '', type: '' },
  records: [],
  collectors: [],
  loading: false,
  searchQuery: '',
  page: 1,
  limit: 25,
  totalRecords: 0,
  filterCollector: '',
  selectedRecords: [],
};

export const fetchCollectors = createAsyncThunk(
  'hearing/fetchCollectors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/collectors');
      return response.data || [];
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not fetch the list of collectors.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchRecords = createAsyncThunk(
  'hearing/fetchRecords',
  async (_, { getState, rejectWithValue }) => {
    const { hearing } = getState();
    try {
      const params = { page: hearing.page, limit: hearing.limit };
      if (hearing.filterCollector) params.collectorId = hearing.filterCollector;
      if (hearing.searchQuery) params.search = hearing.searchQuery;

      const response = await api.get('/records', { params });
      return {
        records: response.data.data || [],
        total: response.data.total || 0,
      };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to fetch records.';
      return rejectWithValue({ message });
    }
  }
);

const hearingSlice = createSlice({
  name: 'hearing',
  initialState,
  reducers: {
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
    setViewingRecordId(state, action) {
      state.viewingRecordId = action.payload;
    },
    setMessage(state, action) {
      state.message = action.payload;
    },
    clearMessage(state) {
      state.message = { text: '', type: '' };
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setFilterCollector(state, action) {
      state.filterCollector = action.payload;
    },
    setSelectedRecords(state, action) {
      state.selectedRecords = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollectors.fulfilled, (state, action) => {
        state.collectors = action.payload;
      })
      .addCase(fetchRecords.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        state.records = action.payload.records;
        state.totalRecords = action.payload.total;
        state.loading = false;
        state.selectedRecords = [];
      })
      .addCase(fetchRecords.rejected, (state) => {
        state.records = [];
        state.totalRecords = 0;
        state.loading = false;
      });
  },
});

export const showMessage = (text, type = 'error', duration = 5000) => (dispatch) => {
  dispatch(setMessage({ text, type }));
  setTimeout(() => dispatch(clearMessage()), duration);
};

export const {
  setViewMode,
  setViewingRecordId,
  setMessage,
  clearMessage,
  setSearchQuery,
  setPage,
  setLimit,
  setFilterCollector,
  setSelectedRecords,
} = hearingSlice.actions;

export const selectHearingViewMode = (state) => state.hearing.viewMode;
export const selectHearingViewingRecordId = (state) => state.hearing.viewingRecordId;
export const selectHearingMessage = (state) => state.hearing.message;
export const selectHearingRecords = (state) => state.hearing.records;
export const selectHearingCollectors = (state) => state.hearing.collectors;
export const selectHearingLoading = (state) => state.hearing.loading;
export const selectHearingSearchQuery = (state) => state.hearing.searchQuery;
export const selectHearingPage = (state) => state.hearing.page;
export const selectHearingLimit = (state) => state.hearing.limit;
export const selectHearingTotalRecords = (state) => state.hearing.totalRecords;
export const selectHearingFilterCollector = (state) => state.hearing.filterCollector;
export const selectHearingSelectedRecords = (state) => state.hearing.selectedRecords;

export default hearingSlice.reducer;
